import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsApi, jobsApi, formatApiError } from '../../lib/api';
import { Loader2, Target, TrendingUp, AlertCircle } from 'lucide-react';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

// Stage colour by category (pipeline = blue/indigo gradient by progression).
const STAGE_COLORS = {
    new: '#3B82F6',
    to_review: '#0EA5E9',
    shortlisted: '#6366F1',
    interview_scheduled: '#8B5CF6',
    interview_completed: '#A855F7',
    technical_test: '#D946EF',
    offer_sent: '#10B981',
    rejected: '#EF4444',
};

export default function PipelineHealthSection() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [jobFilter, setJobFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Lazy-load jobs once for the filter dropdown.
        jobsApi.getAll()
            .then((res) => setJobs((res.data?.jobs || res.data || []).slice(0, 200)))
            .catch(() => setJobs([]));
    }, []);

    useEffect(() => {
        const params = jobFilter === 'all' ? {} : { job_id: jobFilter };
        setLoading(true);
        analyticsApi.getPipelineAnalytics(params)
            .then((res) => { setData(res.data); setError(null); })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [jobFilter]);

    const renderConversionCard = (key, label, value, accent) => (
        <div
            key={key}
            data-testid={`pipeline-rate-${key}`}
            className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]"
        >
            <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full`} style={{ background: accent }} />
                <span className="text-xs font-medium text-[#4B5563]">{label}</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">{value.toFixed(1)}%</p>
        </div>
    );

    return (
        <div
            className="bg-white p-6 rounded-lg border border-[#E5E7EB]"
            data-testid="pipeline-health-section"
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#002FA7]" />
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('pipeline_analytics.title')}
                    </h2>
                </div>
                {jobs.length > 0 && (
                    <Select value={jobFilter} onValueChange={setJobFilter}>
                        <SelectTrigger className="w-full sm:w-[260px]" data-testid="pipeline-job-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('pipeline_analytics.all_jobs')}</SelectItem>
                            {jobs.map((j) => (
                                <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#9CA3AF]">
                    <Loader2 className="w-6 h-6 animate-spin" />
                </div>
            ) : error ? (
                <div className="flex items-start gap-2 p-3 rounded-md bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span className="text-sm">{error}</span>
                </div>
            ) : data && data.with_pipeline_tag === 0 ? (
                <div className="text-center py-10 text-[#9CA3AF]" data-testid="pipeline-empty-state">
                    <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="font-medium text-[#4B5563]">{t('pipeline_analytics.empty_title')}</p>
                    <p className="text-sm mt-1">{t('pipeline_analytics.empty_desc')}</p>
                </div>
            ) : data ? (
                <div className="space-y-6">
                    {/* Distribution bars */}
                    <div className="space-y-2" data-testid="pipeline-distribution">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-[#4B5563]">
                                {t('pipeline_analytics.distribution_label')}
                            </p>
                            <span className="text-xs text-[#9CA3AF]">
                                {t('pipeline_analytics.candidates_total', {
                                    count: data.with_pipeline_tag,
                                })}
                            </span>
                        </div>
                        {data.distribution.map((row) => {
                            const color = STAGE_COLORS[row.slug] || '#6B7280';
                            const tagLabel = t(`tags.${row.slug}`, { defaultValue: row.slug });
                            return (
                                <div
                                    key={row.slug}
                                    className="group"
                                    data-testid={`pipeline-stage-${row.slug}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium text-[#0A0A0A]">{tagLabel}</span>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-bold" style={{ color }}>
                                                {row.count}
                                            </span>
                                            <span className="text-[11px] text-[#9CA3AF] w-12 text-right">
                                                {row.percent.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2 bg-[#F3F4F6] rounded overflow-hidden">
                                        <div
                                            className="h-full rounded transition-all duration-500"
                                            style={{
                                                width: `${row.percent}%`,
                                                background: color,
                                                minWidth: row.count > 0 ? 4 : 0,
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Conversion rates */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-[#E5E7EB]">
                        {renderConversionCard(
                            'shortlist_to_interview',
                            t('pipeline_analytics.rate_shortlist_to_interview'),
                            data.conversion_rates.shortlist_to_interview,
                            '#6366F1',
                        )}
                        {renderConversionCard(
                            'interview_to_offer',
                            t('pipeline_analytics.rate_interview_to_offer'),
                            data.conversion_rates.interview_to_offer,
                            '#A855F7',
                        )}
                        {renderConversionCard(
                            'overall',
                            t('pipeline_analytics.rate_overall'),
                            data.conversion_rates.overall,
                            '#10B981',
                        )}
                    </div>

                    {/* Footer line */}
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#6B7280] pt-2">
                        <span>
                            {t('pipeline_analytics.untagged_label', { count: data.untagged })}
                        </span>
                        <span>
                            {t('pipeline_analytics.rejected_label', { count: data.rejected })}
                        </span>
                        {data.filtered_by_job_id && (
                            <span className="font-medium text-[#002FA7]">
                                {t('pipeline_analytics.filtered_active')}
                            </span>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
